import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  Search,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Eye,
  X,
  Loader2
} from 'lucide-react'
import { pointsApi } from '../../lib/api'

const UserEvents = () => {
  usePageTitle()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
    loadRecords()
  }, [])

  const loadRecords = () => {
    setLoading(true)
    pointsApi.records({ page_size: 100 })
      .then((res: any) => {
        setRecords(res.records || [])
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false))
  }

  const filteredRecords = records.filter(record => {
    const userName = record.user_name || ''
    const userPhone = record.user_phone || ''
    const eventType = record.event_type_name || ''
    const matchesSearch =
      userName.includes(searchTerm) ||
      userPhone.includes(searchTerm) ||
      eventType.includes(searchTerm)
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'confirmed': return { label: '已确认', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', textColor: 'text-green-700' }
      case 'pending': return { label: '待确认', icon: Clock, color: 'text-orange-600', bg: 'bg-orange-100', textColor: 'text-orange-700' }
      case 'cancelled': return { label: '已撤销', icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', textColor: 'text-red-700' }
      default: return { label: status, icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100', textColor: 'text-gray-700' }
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-6">
      {/* 顶部导航 */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-4 flex items-center space-x-4 sticky top-0 z-10">
        <Link to="/user/profile" className="p-2 hover:bg-white/20 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-white" />
        </Link>
        <h1 className="text-lg font-bold text-white">事件记录</h1>
        {loading && <Loader2 className="w-5 h-5 text-white animate-spin ml-auto" />}
      </div>

      {/* 搜索 */}
      <div className="px-4 py-3 bg-white">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="搜索用户姓名、手机号、事件类型..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50"
          />
        </div>
        {/* 筛选按钮 */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {['all', 'pending', 'confirmed', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${statusFilter === status ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {status === 'all' ? '全部' : status === 'pending' ? '待确认' : status === 'confirmed' ? '已确认' : '已撤销'}
            </button>
          ))}
        </div>
      </div>

      {/* 列表 */}
      <div className="px-4 mt-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-400">暂无记录</div>
        ) : filteredRecords.map(record => {
          const statusConfig = getStatusConfig(record.status)
          const StatusIcon = statusConfig.icon
          // 后端返回平铺字段: user_name, user_phone, event_type_name, operator_name, hospital_name
          const userName = record.user_name || record.user?.name || ''
          const userPhone = record.user_phone || record.user?.phone || ''
          const eventName = record.event_type_name || record.eventType || record.event_type?.name || ''
          return (
            <div key={record.id} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-700 font-bold text-sm">{userName.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{userName}</p>
                    <p className="text-xs text-gray-400">{userPhone}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.textColor}`}>
                  <StatusIcon className={`w-3 h-3 mr-1 ${statusConfig.color}`} />
                  {statusConfig.label}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-500">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{eventName}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-green-600">+{record.points || 0}</span>
                  <span className="text-xs text-gray-400 ml-2">{record.created_at || record.createdAt || ''}</span>
                </div>
              </div>
              {/* 操作按钮 */}
              <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-50 space-x-2">
                <button
                  onClick={() => setSelectedRecord(record)}
                  className="flex items-center space-x-1 px-3 py-1.5 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
                >
                  <Eye className="w-4 h-4" />
                  <span>查看详情</span>
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* 详情弹窗 */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="bg-white rounded-t-3xl w-full max-w-md px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">事件详情</h3>
              <button onClick={() => setSelectedRecord(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['用户姓名', selectedRecord.user_name || selectedRecord.user?.name || ''],
                ['手机号', selectedRecord.user_phone || selectedRecord.user?.phone || ''],
                ['事件类型', selectedRecord.event_type_name || selectedRecord.eventType || selectedRecord.event_type?.name || ''],
                ['获得积分', `+${selectedRecord.points || 0}`],
                ['机构', selectedRecord.hospital_name || selectedRecord.hospital || ''],
                ['操作员', selectedRecord.operator_name || selectedRecord.operatorName || ''],
                ['创建时间', selectedRecord.created_at || selectedRecord.createdAt || ''],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              {selectedRecord.status === 'pending' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                  <Clock className="w-3 h-3 mr-1" />待确认
                </span>
              )}
              {selectedRecord.status === 'confirmed' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />已确认
                </span>
              )}
            </div>
            <button
              onClick={() => setSelectedRecord(null)}
              className="w-full mt-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium"
            >
              关闭
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserEvents